/*
* User utilities
*/

import * as React from 'react';
import moment from 'moment';
import * as _ from 'lodash';
import { Link } from 'react-router-dom';
import { Tooltip, OverlayTrigger, Button, ListGroup, ListGroupItem } from 'react-bootstrap';

import { PortalRoles } from '../invite/create-invite';
import AppConfiguration from '../config/configuration';
import { Formatters } from '../common/formatters';
import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';

export const HorizontalDescriptionList = ({ title, value, classNameContainer = 'dl-horizontal', classNameTitle = '', classNameValue = '', ...rest }) => (
    <div className={`hdl ${classNameContainer}`}>
        <div className={`hdl-dt ${classNameTitle}`}>{`${title}${(title) ? ':' : ''}`}</div>
        <div className={`hdl-dd ${classNameValue}`}>{value}{rest.children}</div>
    </div>
);

export class UserUtilities {
    public static GetRoleText(roleName: string) {
        switch (roleName) {
            case PortalRoles.PortalAdmin.RoleName:
                return PortalRoles.PortalAdmin.RoleText;
            case PortalRoles.PortalUser.RoleName:
                return PortalRoles.PortalUser.RoleText;
            default:
                return '';
        }
    }

    public static IsPortalInviteValid(portalInvite: any, associationId: any = 0) {
        const inactive = (_.get(portalInvite, 'inactive', false)) ? true : false;
        const expireDate = _.get(portalInvite, 'roleExpirationDate');
        const isExpired = (expireDate !== null) ? moment(expireDate).isBefore(moment()).valueOf() : false;
        const assocId = Number.parseInt(associationId, 10);
        const isAssoc = (assocId > 0) ? Formatters.getInt(portalInvite, 'association.associationId') === assocId : true;
        const result = (!inactive && !isExpired && isAssoc);
        // console.log(`${_.get(o, 'association.name', '')} inactive ${inactive} expireDate ${expireDate} isExpired ${isExpired} associationId ${associationId} isAssoc ${isAssoc} result ${result}`);
        return result;
    }

    public static GetValidPortalInvites(portalInvites: any, associationId: any = 0) {
        return _.filter(portalInvites, (o) => this.IsPortalInviteValid(o, associationId));
    }

    public static GetInvalidPortalInvites(portalInvites: any) {
        return _.filter(portalInvites, (o) => {
            const inactive = (_.get(o, 'inactive', false)) ? true : false;
            const expireDate = _.get(o, 'roleExpirationDate');
            const isExpired = (expireDate !== null) ? moment(expireDate).isBefore(moment()).valueOf() : false;

            const result = (inactive || isExpired);
            return result;
        });
    }

    public static FilterPortalInvites(portalInvites: any, roleName: string) {
        return _.filter(portalInvites, { 'roleName': roleName });
    }

    public static BuildExpiresOn(expireDate: any) {
        const expiresOn = (expireDate) ? moment(expireDate).format(AppConfiguration.getMomentShortDateFormat()) : '';
        const prependText = (expireDate && moment(expireDate).isBefore(moment())) ? 'Expired' : 'Expires';

        return (expiresOn) ? `${prependText} on ${expiresOn}` : 'Never expires';
    }

    public static BuildInviteLinkToAssociation(portalInvite: any, assocLinkTo: string, prependAssocId: boolean = false) {
        const expireDate = _.get(portalInvite, 'roleExpirationDate');

        const tooltip = (<Tooltip className="in" id={`tooltip-expires-${_.get(portalInvite, '_id', '')}`}>{this.BuildExpiresOn(expireDate)}</Tooltip>);
        const assocLinkClass = 'glyphicon glyphicon-chevron-right';

        const assocLink = (
            <Link to={assocLinkTo}>
                <i className={assocLinkClass} /> {Formatters.composeAssociationNameLabel(_.get(portalInvite, 'association'), prependAssocId)}
            </Link>
        );

        return (
            <HorizontalDescriptionList
                title=""
                value=""
                classNameContainer="dl-horizontal dl-invite-links dl-invite-links-assoc"
                classNameValue=""
                key={`hdl-portalinvite-assoc-${_.get(portalInvite, '_id', '')}`}
            >
                <OverlayTrigger
                    overlay={tooltip}
                    placement="bottom"
                    delayShow={0}
                    delayHide={0}
                >
                    {assocLink}
                </OverlayTrigger>
            </HorizontalDescriptionList>
        );
    }

    public static BuildEmptyPortalInvites(assocId: number, roleName: string, text: string = 'None assigned') {
        return (
            <div key={`assoc-${assocId}-portal-invites-${roleName}-none`} className="manage-portal-access-none-assigned">{text}</div>
        );
    }

    public static BuildInviteLinkToUserProfile(portalInvite: any, userLinkTo: string, userDeactive: boolean = false, prependToggleIcon: boolean = false) {
        const expireDate = _.get(portalInvite, 'roleExpirationDate');

        const tooltip = (<Tooltip className="in" id={`tooltip-expires-${_.get(portalInvite, '_id', '')}`}>{this.BuildExpiresOn(expireDate)}</Tooltip>);
        const userLinkClass = (userDeactive) ? 'fa fa-toggle-off' : 'fa fa-toggle-on';
        const userLinkPrepend = (prependToggleIcon) ? (<i className={userLinkClass} />) : undefined;

        const userLink = (
            <Link to={userLinkTo}>
                {userLinkPrepend}{Formatters.composeUsernameLabel(_.get(portalInvite, 'user'), true)}
            </Link>
        );

        return (
            <HorizontalDescriptionList
                title=""
                value=""
                classNameContainer={`dl-horizontal dl-invite-links dl-invite-links-user ${(userDeactive) ? 'dl-invite-links-user-deactive' : ''}`}
                classNameValue=""
                key={`hdl-portalinvite-${_.get(portalInvite, 'association.associationObjectId', '')}-${_.get(portalInvite, 'user.userObjectId', '')}`}
            >
                <OverlayTrigger
                    overlay={tooltip}
                    placement="bottom"
                    delayShow={0}
                    delayHide={0}
                >
                    {userLink}
                </OverlayTrigger>
            </HorizontalDescriptionList>
        );
    }

    public static BuildInviteButtonAction(portalInvite: any, userDeactive: boolean = false, buttonDisabled: boolean = false, handleClick: (e: any) => void = (e) => null) {
        const expireDate = _.get(portalInvite, 'roleExpirationDate');

        const tooltip = (<Tooltip className="in" id={`tooltip-expires-${_.get(portalInvite, '_id', '')}`}>{this.BuildExpiresOn(expireDate)}</Tooltip>);
        const userLinkClass = (userDeactive) ? 'fa fa-toggle-off' : 'fa fa-toggle-on';

        const userButton = (
            <Button bsStyle="link" disabled={buttonDisabled} onClick={(e) => { handleClick(portalInvite); }}>
                <i className={userLinkClass} /> {Formatters.composeUsernameLabel(_.get(portalInvite, 'user'), true)}
            </Button>
        );

        return (
            <HorizontalDescriptionList
                title=""
                value=""
                classNameContainer={`dl-horizontal dl-invite-links dl-invite-links-user ${(userDeactive) ? 'dl-invite-links-user-deactive' : ''}`}
                classNameValue=""
                key={`hdl-portalinvite-${_.get(portalInvite, '_id', '')}`}
            >
                <OverlayTrigger
                    overlay={tooltip}
                    placement="bottom"
                    delayShow={0}
                    delayHide={0}
                >
                    {userButton}
                </OverlayTrigger>
            </HorizontalDescriptionList>
        );
    }

    public static BuildUpdatedOn(updateDate: any) {
        // const updatedOn = (updateDate) ? moment(updateDate).format(AppConfiguration.getMomentShortDateFormat()) : '';        
        const updatedOn = (updateDate) ? moment(updateDate).fromNow() : '';
        return (updatedOn) ? `Updated ${updatedOn}` : 'Never updated';
    }

    public static BuildScenarioTiledAction(scenario: any, myScenario: boolean = false, handleClick: (e: any) => void = (e) => null, showOverlayTooltip: boolean = false) {
        const updateDate = _.get(scenario, 'lastUpdate');

        const tooltip = (<Tooltip className="in" id={`tooltip-updated-${_.get(scenario, '_id', '')}`}>{this.BuildUpdatedOn(updateDate)}</Tooltip>);
        const userLinkClass = (myScenario) ? 'fa fa-pencil' : 'fa fa-eye';

        const buttonDisabled = false;

        const userButton = (
            <Button bsStyle="link" disabled={buttonDisabled} onClick={(e) => { handleClick(scenario); }}>
                <i className={userLinkClass} /> {Formatters.getStr(scenario, 'name')}
            </Button>
        );

        const buttonContainer = (showOverlayTooltip) ? (
            <OverlayTrigger
                overlay={tooltip}
                placement="bottom"
                delayShow={0}
                delayHide={0}
            >
                {userButton}
            </OverlayTrigger>
        ) : userButton;

        return (
            <Col xs={6} key={`hdl-report-scenario-${_.get(scenario, '_id', '')}`}>
                <HorizontalDescriptionList
                    title=""
                    value=""
                    classNameContainer={`dl-horizontal dl-scenario-links dl-scenarios ${(buttonDisabled) ? 'dl-invite-links-user-deactive' : ''}`}
                    classNameValue=""
                >
                    {buttonContainer}
                </HorizontalDescriptionList>
            </Col>
        );
    }

    public static BuildScenarioAppend(text: any, wrapEmbedParens: boolean = false, wrapCSSParens: boolean = false) {
        const caption = (wrapEmbedParens) ? Formatters.wrapParentheses(text) : text;

        return (text) ? (<span className={`dl-invite-links-append ${(wrapCSSParens) ? '' : 'text-parenthesize'}`}>{caption}</span>) : (<span />);
    }
    public static BuildScenarioTiledButtonAction(
        scenario: any,
        myScenario: boolean = false,
        handleClick: (e: any) => void = (e) => null,
        appendAuthor: boolean = false,
        appendShared: boolean = false,
        textShared: string = 'Shared',
        textNotShared: string = 'Not Shared',
    ) {
        const userLinkClass = (myScenario) ? 'fa fa-pencil' : 'fa fa-eye';

        const buttonDisabled = false;

        const author = _.get(scenario, '_info.author');
        const authorText = Formatters.composeUsernameLabel(author, true);
        const sharedText = (_.get(scenario, 'private', true)) ? textNotShared : textShared;
        const scenarioAuthorSection = (appendAuthor) ? (this.BuildScenarioAppend(authorText, false, true)) : undefined;
        const scenarioSharedSection = (appendShared) ? (this.BuildScenarioAppend(sharedText, false, false)) : undefined;

        const scenarioButtonContainer = (
            <div>
                <Button bsStyle="link" disabled={buttonDisabled} onClick={(e) => { handleClick(scenario); }}>
                    <i className={userLinkClass} /> {Formatters.getStr(scenario, 'name')}
                </Button>
                {scenarioAuthorSection}
                {scenarioSharedSection}
            </div>
        );

        return (
            <HorizontalDescriptionList
                key={`hdl-report-scenario-hdl-${_.get(scenario, '_id', '')}`}
                title=""
                value=""
                classNameContainer={`dl-horizontal dl-scenario-links dl-scenarios ${(buttonDisabled) ? 'dl-invite-links-user-deactive' : ''}`}
                classNameValue=""
            >
                {scenarioButtonContainer}
            </HorizontalDescriptionList>
            // <Col xs={colXs} key={`hdl-report-scenario-${_.get(scenario, '_id', '')}`}>
            //     <HorizontalDescriptionList
            //         title=""
            //         value=""
            //         classNameContainer={`dl-horizontal dl-scenario-links dl-scenarios ${(buttonDisabled) ? 'dl-invite-links-user-deactive' : ''}`}
            //         classNameValue=""                    
            //     >
            //         {scenarioButtonContainer}
            //     </HorizontalDescriptionList>           
            // </Col>     
        );
    }
}



// WEBPACK FOOTER //
// ./src/user/user-utilities.tsx